// src/features/eventos/hooks/useEventosCatalogos.js
import { useEffect, useMemo, useState } from "react";
import { getToken } from "../../../services/authService";

export function useEventosCatalogos(apiUrl) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [personas, setPersonas] = useState([]);
    const [platos, setPlatos] = useState([]);
    const [vinos, setVinos] = useState([]);
    const [habitaciones, setHabitaciones] = useState([]);
    const [roles, setRoles] = useState([]);
    const [armas, setArmas] = useState([]);
    const [adminPersonaIds, setAdminPersonaIds] = useState(new Set());

    const [tiposCaceria, setTiposCaceria] = useState([]);
    const [tiposEvento, setTiposEvento] = useState([]);

    useEffect(() => {
        let alive = true;

        (async () => {
            setLoading(true);
            setError("");

            try {
                const token = getToken();
                const headers = { Authorization: `Bearer ${token}` };

                const [
                    personasRes,
                    platoRes,
                    vinosRes,
                    habitacionesRes,
                    rolesRes,
                    armasRes,
                    adminsRes,
                    tiposCaceriaRes,
                    tiposEventoRes,
                ] = await Promise.all([
                    fetch(`${apiUrl}/api/personas/activas`, { headers }),
                    fetch(`${apiUrl}/api/plato/all`, { headers }),
                    fetch(`${apiUrl}/api/vinos/all`, { headers }),
                    fetch(`${apiUrl}/api/habitaciones/disponibles`, { headers }),
                    fetch(`${apiUrl}/api/roles/all`, { headers }),
                    fetch(`${apiUrl}/api/armas/all`, { headers }),
                    fetch(`${apiUrl}/api/auth/admin/persona-ids`, { headers }),
                    fetch(`${apiUrl}/api/enums/tipo-caceria`, { headers }),
                    fetch(`${apiUrl}/api/enums/tipo-evento`, { headers }),
                ]);

                // Opcional: validación mínima de responses
                const bad = [
                    personasRes,
                    platoRes,
                    vinosRes,
                    habitacionesRes,
                    rolesRes,
                    armasRes,
                    adminsRes,
                    tiposCaceriaRes,
                    tiposEventoRes,
                ].find((r) => !r.ok);

                if (bad) {
                    throw new Error(`HTTP ${bad.status}`);
                }

                const [
                    personasData,
                    platosData,
                    vinosData,
                    habitacionesData,
                    rolesData,
                    armasData,
                    adminsData,
                    tiposCaceriaData,
                    tiposEventoData,
                ] = await Promise.all([
                    personasRes.json(),
                    platoRes.json(),
                    vinosRes.json(),
                    habitacionesRes.json(),
                    rolesRes.json(),
                    armasRes.json(),
                    adminsRes.json(),
                    tiposCaceriaRes.json(),
                    tiposEventoRes.json(),
                ]);

                if (!alive) return;

                setPersonas(Array.isArray(personasData) ? personasData : []);
                setPlatos(Array.isArray(platosData) ? platosData : []);
                setVinos(Array.isArray(vinosData) ? vinosData : []);
                setHabitaciones(Array.isArray(habitacionesData) ? habitacionesData : []);
                setRoles(Array.isArray(rolesData) ? rolesData : []);
                setArmas(Array.isArray(armasData) ? armasData : []);
                setAdminPersonaIds(new Set(Array.isArray(adminsData) ? adminsData : []));

                setTiposCaceria(Array.isArray(tiposCaceriaData) ? tiposCaceriaData : []);
                setTiposEvento(Array.isArray(tiposEventoData) ? tiposEventoData : []);
            } catch (e) {
                if (!alive) return;
                console.error(e);
                setError("No se pudieron cargar los catálogos del evento.");
            } finally {
                if (!alive) return;
                setLoading(false);
            }
        })();

        return () => {
            alive = false;
        };
    }, [apiUrl]);

    // Maps memoizados (los que ya estabas usando en AñadirEvento.js)
    const personasById = useMemo(() => {
        return new Map((personas || []).map((p) => [String(p.id), p]));
    }, [personas]);

    const rolesById = useMemo(() => {
        return new Map((roles || []).map((r) => [String(r.id), r]));
    }, [roles]);

    const habitacionesById = useMemo(() => {
        return new Map((habitaciones || []).map((h) => [String(h.id), h]));
    }, [habitaciones]);

    const platosById = useMemo(() => {
        return new Map((platos || []).map((p) => [String(p.id), p]));
    }, [platos]);

    return {
        loading,
        error,

        personas,
        platos,
        vinos,
        habitaciones,
        roles,
        armas,
        adminPersonaIds,

        tiposCaceria,
        tiposEvento,

        personasById,
        rolesById,
        habitacionesById,
        platosById,
    };
}
